import { useEffect, type RefObject } from 'react';

/**
 * Selector for what the browser considers tabbable inside a dialog.
 *
 * `[tabindex]:not([tabindex="-1"])` is included so custom widgets opting into
 * the tab order are trapped too; `disabled` and `hidden` elements are
 * excluded because they are not focusable and would create dead stops in the
 * cycle.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableWithin(container: HTMLElement): readonly HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

export interface UseOverlayOptions {
  readonly open: boolean;
  readonly containerRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
}

/**
 * The shared behaviour of `Modal` and `Drawer`: focus trapping, focus
 * restoration, Escape-to-close and background scroll locking.
 *
 * This lives in one place because these four concerns are only correct
 * together. A dialog that traps focus but never restores it strands the
 * keyboard user at the top of the document on close; one that restores focus
 * but does not trap lets Tab walk into the inert page behind the scrim. §7.1
 * states the contract as a single requirement — "dialogs/drawers trap focus
 * and restore it" — and it is implemented here as a single unit rather than
 * re-derived per component.
 *
 * Focus is moved to the dialog container itself (which carries `tabIndex={-1}`)
 * rather than to its first focusable child: landing on the first control skips
 * the dialog's own title, which is exactly the context a screen-reader user
 * needs to know what just opened.
 */
export function useOverlay({ open, containerRef, onClose }: UseOverlayOptions): void {
  useEffect(() => {
    if (!open) return;

    const container = containerRef.current;
    if (container === null) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    container.focus();

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || container === null) return;

      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        // Nothing to cycle between: keep focus on the dialog rather than
        // letting Tab escape to the page behind the scrim.
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      body.style.overflow = previousOverflow;
      // Only restore if the element is still in the document; it may have been
      // unmounted while the dialog was open (e.g. the row that opened it was
      // deleted), and focusing a detached node silently sends focus to <body>.
      if (previouslyFocused?.isConnected === true) {
        previouslyFocused.focus();
      }
    };
  }, [open, containerRef, onClose]);
}
